import { Platform, Linking, Vibration } from 'react-native';
import { Camera } from 'react-native-vision-camera';
import { PERMISSIONS, request, check, RESULTS } from 'react-native-permissions';

/**
 * BarcodeScannerService
 * 
 * Regelt alles met de scanner:
 * - Mag ik je camera zien?
 * - Is dit wel een echte barcode?
 * - Trillen als het gelukt is (bzzzt)
 * - Snapt QR codes en streepjescodes
 */
class BarcodeScannerService {
  /**
   * Mag ik alsjeblieft de camera gebruiken?
   * Dit vraagt het aan de gebruiker (pop-up).
   * @returns Belofte<boolean> - true als het mag, anders pech
   */
  async requestPermissions(): Promise<boolean> {
    try {
      const cameraPermission = await Camera.requestCameraPermission();
      
      if (cameraPermission === 'granted') {
        return true;
      }
      
      if (cameraPermission === 'denied') {
        return false;
      }
      
      // Als we het nog steeds niet weten, proberen we het via de permissions lib
      // (Die is soms wat slimmer)
      const permission = Platform.select({
        ios: PERMISSIONS.IOS.CAMERA,
        android: PERMISSIONS.ANDROID.CAMERA,
      });
      
      if (!permission) {
        return false;
      }
      
      const result = await request(permission);
      return result === RESULTS.GRANTED;
    } catch (error) {
      console.error('Fout bij vragen camera toestemming:', error);
      return false;
    }
  }

  /**
   * Checken of we het niet al lang gevraagd hadden.
   * Handig om te weten of we die vervelende pop-up weer moeten laten zien.
   * @returns Belofte<boolean> - true als alles ok is
   */
  async hasPermissions(): Promise<boolean> {
    try {
      const cameraPermission = await Camera.getCameraPermissionStatus();
      
      if (cameraPermission === 'granted') {
        return true;
      }
      
      // Dubbelcheck (zeker weten!)
      const permission = Platform.select({
        ios: PERMISSIONS.IOS.CAMERA,
        android: PERMISSIONS.ANDROID.CAMERA,
      });
      
      if (!permission) {
        return false;
      }
      
      const result = await check(permission);
      return result === RESULTS.GRANTED;
    } catch (error) {
      console.error('Fout bij checken camera toestemming:', error);
      return false;
    }
  }

  /**
   * Instellingen openen zodat ze het zelf aan kunnen zetten
   * (Als ze per ongeluk op 'nee' hebben geklikt)
   */
  async openSettings(): Promise<void> {
    try {
      await Linking.openSettings();
    } catch (error) {
      console.error('Fout bij openen instellingen:', error);
    }
  }

  /**
   * Klopt deze barcode wel?
   * We ondersteunen alleen de 'grote' standaarden (EAN-13, UPC, QR).
   * @param code - Wat de camera denkt te zien
   * @returns boolean - true als het een code is die we kennen
   */
  validateBarcodeFormat(code: any): boolean {
    const supportedFormats = ['ean-13', 'upc-a', 'upc-e', 'qr'];
    
    if (!code.type) {
      return false;
    }
    
    const format = code.type.toLowerCase();
    return supportedFormats.includes(format);
  }

  /**
   * Barcode netjes maken
   * @param code - De scan
   * @returns string | null - De schone barcode of niks als het troep is
   */
  formatBarcode(code: any): string | null {
    if (!code.value || !this.validateBarcodeFormat(code)) {
      return null;
    }
    
    const barcode = code.value.trim();
    
    // Checken of de lengte klopt (anders hebben we er niks aan)
    const format = code.type?.toLowerCase();
    
    switch (format) {
      case 'ean-13':
        // EAN-13 moet 13 cijfers zijn
        if (!/^\d{13}$/.test(barcode)) {
          return null;
        }
        break;
      
      case 'upc-a':
        // UPC-A moet 12 cijfers zijn
        if (!/^\d{12}$/.test(barcode)) {
          return null;
        }
        break;
      
      case 'upc-e':
        // UPC-E moet 6-8 cijfers zijn
        if (!/^\d{6,8}$/.test(barcode)) {
          return null;
        }
        // UPC-E is een soort ingekorte UPC-A, moet dus langer gemaakt worden
        return this.convertUPCEtoUPCA(barcode);
      
      case 'qr':
        // QR codes kunnen vanalles bevatten, check gewoon of het niet leeg is
        if (barcode.length === 0) {
          return null;
        }
        break;
      
      default:
        return null;
    }
    
    return barcode;
  }

  /**
   * UPC-E (kort) ombouwen naar UPC-A (lang).
   * Vraag me niet hoe dit werkt, ik heb het van internet, maar het maakt
   * van die korte Amerikaanse codes normale lange codes.
   * Het checkt het laatste cijfer en voegt dan nullen toe op magische plekken.
   */
  private convertUPCEtoUPCA(upcE: string): string {
    // Voorloopnullen weghalen en zorgen dat we 6 cijfers hebben
    const digits = upcE.replace(/^0+/, '').padStart(6, '0');
    
    if (digits.length !== 6) {
      return upcE; // Als het niet lukt geven we gewoon het origineel terug
    }
    
    // UPC-E naar UPC-A magie
    const lastDigit = digits[5];
    let upcA = '0'; // UPC-A begint altijd met 0
    
    switch (lastDigit) {
      case '0':
      case '1':
      case '2':
        upcA += digits.substring(0, 2) + lastDigit + '0000' + digits.substring(2, 5);
        break;
      case '3':
        upcA += digits.substring(0, 3) + '00000' + digits.substring(3, 5);
        break;
      case '4':
        upcA += digits.substring(0, 4) + '00000' + digits[4];
        break;
      default:
        upcA += digits.substring(0, 5) + '0000' + lastDigit;
        break;
    }
    
    // Check digit toevoegen (simpel gemaakt - in echt moet je dit berekenen)
    return upcA + '0';
  }

  /**
   * Bzzzt! (Trillen als het gelukt is)
   * Dan weet je dat je goed gemikt hebt
   */
  triggerHapticFeedback(): void {
    try {
      if (Platform.OS === 'ios') {
        // iOS: Subtiel trilling
        Vibration.vibrate(20);
      } else {
        // Android: BZZZT
        Vibration.vibrate(100);
      }
    } catch (error) {
      console.error('Fout bij haptic feedback:', error);
    }
  }

  /**
   * Is er überhaupt een camera?
   * @returns Belofte<boolean> - true als we foto's kunnen maken
   */
  async isCameraAvailable(): Promise<boolean> {
    try {
      const devices = await Camera.getAvailableCameraDevices();
      return devices.length > 0;
    } catch (error) {
      console.error('Fout bij checken camera beschikbaarheid:', error);
      return false;
    }
  }
}

// Singleton exporteren (eentje is genoeg)
export default new BarcodeScannerService();
